import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { fireConfettiAt, fireFireworks, CelebrationOverlay } from './CourseCelebration';
import {
  BookOpen, Plus, Trash2, Pencil, Save, Camera, Upload, Download, Check, Eye, Video,
  ChevronLeft, ArrowUp, ArrowDown, X, AlertCircle, Loader2, Bold, Italic, List as ListIcon, Type,
  Link2, Smile, Film
} from 'lucide-react';
import { Modal, SearchBar, matchesSearch, formatDateShort, getRoleLabel, EMOJI_CATS, uploadFileChunked, UploadProgress, prepareImageFile, confirmDialog, alertDialog, promptDialog, saveSelection, restoreSelection, SafeHtml } from './shared';

const isVideoFile = (name) => /\.(mp4|webm|mov|m4v|ogg)$/i.test(name || '');

// Sorgt dafuer, dass vor/nach Videos & Iframes im Editor immer eine beschreibbare Zeile existiert
const normalizeEditorMedia = (el) => {
  if (!el) return;
  const mkP = () => { const p = document.createElement('p'); p.innerHTML = '<br>'; return p; };
  const fixEmpty = (n) => { if (n && n.nodeType === 1 && !n.textContent.trim() && !n.querySelector('br, img, video, iframe')) n.appendChild(document.createElement('br')); };
  el.querySelectorAll('video, iframe').forEach(v => {
    let block = v;
    while (block.parentElement && block.parentElement !== el) block = block.parentElement;
    if (block.parentElement !== el) return;
    const prev = block.previousElementSibling;
    if (!prev || prev.matches('video, iframe') || prev.querySelector('video, iframe')) el.insertBefore(mkP(), block);
    else fixEmpty(prev);
    const next = block.nextElementSibling;
    if (!next) el.appendChild(mkP());
    else fixEmpty(next);
  });
};

export default function CoursesTab({
  user, authHeaders, API_URL, isAdmin, isAdminOrCoach,
  courses, setCourses, searchTerm, setSearchTerm
}) {
  const autoLinkUrls = (html) => {
    if (!html) return '';
    // Fremde Textfarben aus Zwischenablage-Formatierung entfernen
    html = html.replace(/(background-color|color)\s*:\s*[^;"']+;?/gi, '');
    // DOM-basierte Auto-Verlinkung (erkennt auch URLs am Zeilenanfang)
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const urlRe = /https?:\/\/[^\s<>"']+/g;
    nodes.forEach(node => {
      if (node.parentElement && node.parentElement.closest('a')) return;
      const val = node.nodeValue;
      if (!/https?:\/\//.test(val)) return;
      const frag = document.createDocumentFragment();
      let last = 0, m;
      urlRe.lastIndex = 0;
      while ((m = urlRe.exec(val)) !== null) {
        let url = m[0];
        const trail = url.match(/[.,;:!?)]+$/);
        const trimmed = trail ? trail[0] : '';
        if (trimmed) url = url.slice(0, -trimmed.length);
        if (m.index > last) frag.appendChild(document.createTextNode(val.slice(last, m.index)));
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noreferrer';
        a.style.color = '#C8A45C';
        a.textContent = url;
        frag.appendChild(a);
        if (trimmed) frag.appendChild(document.createTextNode(trimmed));
        last = m.index + m[0].length;
      }
      if (last < val.length) frag.appendChild(document.createTextNode(val.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
    return tpl.innerHTML;
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    url = url.trim();
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1].substring(0,11)}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return null;
  };

  const getVideoThumb = (url) => {
    if (!url) return null;
    url = url.trim();
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1].substring(0,11)}/mqdefault.jpg`;
    return null;
  };

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseLessons, setCourseLessons] = useState([]);

  // Klick auf ein Video im Editor -> entfernen (Videos sind in contentEditable sonst schwer loeschbar)
  useEffect(() => {
    const onEditorMediaClick = async (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const vid = t.closest('.wysiwyg-editor video');
      if (!vid) return;
      e.preventDefault();
      e.stopPropagation();
      if (await confirmDialog('Video aus dem Text entfernen?', { title: 'Video entfernen', confirmLabel: 'Entfernen' })) {
        const editor = vid.closest('.wysiwyg-editor');
        let block = vid;
        while (block.parentElement && block.parentElement !== editor) block = block.parentElement;
        block.remove();
      }
    };
    document.addEventListener('click', onEditorMediaClick, true);
    return () => document.removeEventListener('click', onEditorMediaClick, true);
  }, []);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [courseProgress, setCourseProgress] = useState([]);
  const [celebration, setCelebration] = useState(null);
  const closeCelebration = useCallback(() => setCelebration(null), []);

  // Scroll-Ziele (Handy: Projekt oben, Lektion am Kopf, nach "Erledigt" zurück zur Lektionsübersicht)
  const detailTopRef = useRef(null);
  const sidebarRef = useRef(null);
  const viewerRef = useRef(null);
  const pendingScrollRef = useRef(null);

  useEffect(() => {
    const target = pendingScrollRef.current;
    if (!target) return;
    pendingScrollRef.current = null;
    const el = target === 'top' ? detailTopRef.current : target === 'sidebar' ? sidebarRef.current : viewerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const header = document.querySelector('.member-sticky-header');
      const offset = (header?.offsetHeight || 0) + 12;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  });

  const selectLesson = (lesson) => {
    pendingScrollRef.current = 'viewer';
    setSelectedLesson(lesson);
  };
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [showEditCourse, setShowEditCourse] = useState(false);
  const [showCreateLesson, setShowCreateLesson] = useState(false);
  const [showEditLesson, setShowEditLesson] = useState(false);
  const [courseForm, setCourseForm] = useState({ title: '', short_description: '', description: '', image_url: '', visible_to: ['user', 'sponsor', 'coach'] });
  const [lessonForm, setLessonForm] = useState({ title: '', content: '', video_urls: [], images: [], files: [] });
  const [fileLightbox, setFileLightbox] = useState(null);
  const [editLessonForm, setEditLessonForm] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const openCourse = async (courseId) => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/courses/${courseId}`, { headers: authHeaders });
      pendingScrollRef.current = 'top';
      setSelectedCourse(resp.data.course);
      setCourseLessons(resp.data.lessons || []);
      setCourseProgress(resp.data.progress || []);
      setSelectedLesson(null);
    } catch (e) { console.error(e); }
  };

  const refreshCourses = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/courses`, { headers: authHeaders });
      setCourses(resp.data.courses || []);
    } catch (e) { console.error(e); }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setFormLoading(true); setFormError('');
    const descEl = document.getElementById('create-course-desc');
    const desc = descEl ? descEl.innerHTML : courseForm.description;
    try {
      await axios.post(`${API_URL}/api/community/courses`, {...courseForm, description: desc}, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setCourseForm({ title: '', short_description: '', description: '', image_url: '', visible_to: ['user', 'sponsor', 'coach'] });
      if (descEl) descEl.innerHTML = '';
      setShowCreateCourse(false);
      refreshCourses();
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const handleDeleteCourse = async (courseId) => {
    if (!(await confirmDialog('Projekt und alle Lektionen löschen? Dies kann nicht rückgängig gemacht werden.', { title: 'Projekt löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/courses/${courseId}`, { headers: authHeaders });
      setCourses(courses.filter(c => c.id !== courseId));
      if (selectedCourse?.id === courseId) { setSelectedCourse(null); setCourseLessons([]); }
    } catch (e) { console.error(e); }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    setFormLoading(true); setFormError('');
    const descEl = document.getElementById('edit-course-desc');
    const desc = descEl ? descEl.innerHTML : courseForm.description;
    try {
      await axios.put(`${API_URL}/api/community/courses/${selectedCourse.id}`, {...courseForm, description: desc}, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setShowEditCourse(false);
      openCourse(selectedCourse.id);
      refreshCourses();
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    if (!selectedCourse) return;
    setFormLoading(true); setFormError('');
    const contentEl = document.getElementById('create-lesson-content');
    const content = contentEl ? contentEl.innerHTML : lessonForm.content;
    try {
      await axios.post(`${API_URL}/api/community/courses/${selectedCourse.id}/lessons`, {...lessonForm, content}, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setLessonForm({ title: '', content: '', video_urls: [], images: [], files: [] });
      if (contentEl) contentEl.innerHTML = '';
      setShowCreateLesson(false);
      openCourse(selectedCourse.id);
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const handleEditLesson = async (e) => {
    e.preventDefault();
    setFormLoading(true); setFormError('');
    const contentEl = document.getElementById('edit-lesson-content');
    const content = contentEl ? contentEl.innerHTML : editLessonForm.content;
    try {
      await axios.put(`${API_URL}/api/community/lessons/${editLessonForm.id}`, {...editLessonForm, content}, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setShowEditLesson(false);
      openCourse(selectedCourse.id);
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const handleUploadCourseImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const input = e.target;
    let toUpload;
    try { toUpload = await prepareImageFile(file); } catch (perr) { alertDialog(perr.message, { title: 'Bild nicht geeignet', danger: true }); input.value = ''; return; }
    try {
      const fd = new FormData(); fd.append('file', toUpload);
      const resp = await axios.post(`${API_URL}/api/community/courses/upload-image`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ev => setUploadProg({ name: toUpload.name, loaded: ev.loaded, total: ev.total || toUpload.size }),
      });
      setCourseForm(prev => ({ ...prev, image_url: resp.data.image_url }));
    } catch (e) { alertDialog(e.response?.data?.detail || 'Upload fehlgeschlagen', { title: 'Upload fehlgeschlagen', danger: true }); }
    setUploadProg(null);
    input.value = '';
  };

  // WICHTIG: input.value erst NACH dem Upload zuruecksetzen - Safari gibt die Datei sonst frei, bevor sie gesendet ist
  const handleUploadLessonFile = async (e, isImage, formSetter) => {
    const file = e.target.files[0]; if (!file) return;
    const input = e.target;
    try {
      if (isImage) {
        let toUpload;
        try { toUpload = await prepareImageFile(file); } catch (perr) { alertDialog(perr.message, { title: 'Bild nicht geeignet', danger: true }); input.value = ''; return; }
        const fd = new FormData(); fd.append('file', toUpload);
        const resp = await axios.post(`${API_URL}/api/community/courses/upload-image`, fd, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: ev => setUploadProg({ name: toUpload.name, loaded: ev.loaded, total: ev.total || toUpload.size }),
        });
        formSetter(prev => ({ ...prev, images: [...(prev.images || []), resp.data.image_url] }));
      } else {
        const resp = await uploadFileChunked(file, API_URL, authHeaders, setUploadProg);
        formSetter(prev => ({ ...prev, files: [...(prev.files || []), { url: resp.file_url, name: resp.file_name }] }));
      }
    } catch (e) { alertDialog(e.response?.data?.detail || 'Upload fehlgeschlagen. Bitte erneut versuchen.', { title: 'Upload fehlgeschlagen', danger: true }); }
    setUploadProg(null);
    input.value = '';
  };

  const handleToggleProgress = async (lessonId, sourceEl = null, fromViewer = false) => {
    if (!selectedCourse) return;
    const wasDone = courseProgress.includes(lessonId);
    const wasCourseComplete = courseLessons.length > 0 && courseLessons.every(l => courseProgress.includes(l.id));
    try {
      const resp = await axios.post(`${API_URL}/api/community/courses/${selectedCourse.id}/progress/${lessonId}`, {}, { headers: authHeaders });
      const completed = resp.data.completed_lessons || [];
      setCourseProgress(completed);
      refreshCourses();
      if (wasDone) return;
      fireConfettiAt(sourceEl);
      if (fromViewer) pendingScrollRef.current = 'sidebar';
      const nowComplete = courseLessons.length > 0 && courseLessons.every(l => completed.includes(l.id));
      if (nowComplete && !wasCourseComplete) {
        fireFireworks();
        setCelebration({ title: selectedCourse.title });
      }
    } catch (e) { console.error(e); }
  };

  const handleMoveLesson = async (lessonId, direction) => {
    const idx = courseLessons.findIndex(l => l.id === lessonId);
    if ((direction === -1 && idx <= 0) || (direction === 1 && idx >= courseLessons.length - 1)) return;
    const newOrder = [...courseLessons];
    [newOrder[idx], newOrder[idx + direction]] = [newOrder[idx + direction], newOrder[idx]];
    const ids = newOrder.map(l => l.id);
    try {
      await axios.post(`${API_URL}/api/community/courses/${selectedCourse.id}/reorder`, ids, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setCourseLessons(newOrder);
    } catch (e) { console.error(e); }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!(await confirmDialog('Lektion wirklich löschen? Dies kann nicht rückgängig gemacht werden.', { title: 'Lektion löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/lessons/${lessonId}`, { headers: authHeaders });
      setCourseLessons(courseLessons.filter(l => l.id !== lessonId));
      if (selectedLesson?.id === lessonId) setSelectedLesson(null);
    } catch (e) { console.error(e); }
  };

  const handleMoveCourse = async (courseId, direction) => {
    const filtered = courses.filter(c => matchesSearch(c, searchTerm, ['title', 'description']));
    const idx = filtered.findIndex(c => c.id === courseId);
    if ((direction === -1 && idx <= 0) || (direction === 1 && idx >= filtered.length - 1)) return;
    const newOrder = [...filtered];
    [newOrder[idx], newOrder[idx + direction]] = [newOrder[idx + direction], newOrder[idx]];
    const ids = newOrder.map(c => c.id);
    try {
      await axios.post(`${API_URL}/api/community/courses/reorder`, ids, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      refreshCourses();
    } catch (e) { console.error(e); }
  };

  const [uploadProg, setUploadProg] = useState(null);
  const [showEditorEmoji, setShowEditorEmoji] = useState(false);
  const [editorEmojiCat, setEditorEmojiCat] = useState('Smileys');
  const [editorEmojiTarget, setEditorEmojiTarget] = useState(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [videoDialogUrl, setVideoDialogUrl] = useState('');
  const [videoDialogTarget, setVideoDialogTarget] = useState(null);

  const insertEditorLink = async () => {
    const range = saveSelection();
    const url = await promptDialog('Link-URL eingeben:', { title: 'Link einfügen', inputType: 'url', placeholder: 'https://…', confirmLabel: 'Einfügen' });
    if (!url) return;
    restoreSelection(range);
    document.execCommand('createLink', false, url);
  };

  const getEmbedUrlForInsert = (url) => {
    if (!url) return null;
    url = url.trim();
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1].substring(0,11)}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return null;
  };

  const openVideoDialog = (editorId) => {
    setVideoDialogTarget(editorId);
    setVideoDialogUrl('');
    setShowVideoDialog(true);
  };

  const confirmInsertVideo = () => {
    const embedUrl = getEmbedUrlForInsert(videoDialogUrl);
    const el = videoDialogTarget ? document.getElementById(videoDialogTarget) : null;
    if (el && embedUrl) {
      el.focus();
      document.execCommand('insertHTML', false, `<div><iframe src="${embedUrl}" frameborder="0" allowfullscreen style="width:100%;height:315px;border-radius:8px;margin:0.5rem 0;"></iframe></div><p><br></p>`);
      normalizeEditorMedia(el);
    } else if (el && videoDialogUrl) {
      el.focus();
      document.execCommand('insertHTML', false, `<a href="${videoDialogUrl}" target="_blank" rel="noreferrer" style="color:#C8A45C">${videoDialogUrl}</a>`);
    }
    setShowVideoDialog(false);
    setVideoDialogUrl('');
  };

  const insertEditorImage = async (editorId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      let toUpload;
      try { toUpload = await prepareImageFile(file); } catch (perr) { alertDialog(perr.message, { title: 'Bild nicht geeignet', danger: true }); return; }
      try {
        const fd = new FormData();
        fd.append('file', toUpload);
        const resp = await axios.post(`${API_URL}/api/community/courses/upload-image`, fd, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: ev => setUploadProg({ name: toUpload.name, loaded: ev.loaded, total: ev.total || toUpload.size }),
        });
        const imgUrl = resp.data.image_url.startsWith('/') ? API_URL + resp.data.image_url : resp.data.image_url;
        const el = document.getElementById(editorId);
        if (el) {
          el.focus();
          const range = saveSelection();
          const width = await promptDialog('Bildbreite in % (z.B. 50 für halbe Breite, 100 für volle Breite):', { title: 'Bildbreite', defaultValue: '100', inputType: 'number' });
          const w = parseInt(width) || 100;
          restoreSelection(range);
          document.execCommand('insertHTML', false, `<img src="${imgUrl}" style="width:${Math.min(w, 100)}%;border-radius:8px;margin:0.5rem 0;" /><p><br></p>`);
        }
      } catch (err) { alertDialog('Bild-Upload fehlgeschlagen', { title: 'Upload fehlgeschlagen', danger: true }); }
      setUploadProg(null);
    };
    input.click();
  };

  const insertEditorVideoFile = (editorId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/webm,video/quicktime,video/x-m4v,video/ogg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const resp = await uploadFileChunked(file, API_URL, authHeaders, setUploadProg);
        const vUrl = resp.file_url.startsWith('/') ? API_URL + resp.file_url : resp.file_url;
        const el = document.getElementById(editorId);
        if (el) {
          el.focus();
          document.execCommand('insertHTML', false, `<video controls preload="metadata" playsinline style="width:100%;max-height:420px;border-radius:8px;margin:0.5rem 0;background:#000"><source src="${vUrl}"></video><p><br></p>`);
          normalizeEditorMedia(el);
        }
      } catch (err) { alertDialog(err.response?.data?.detail || 'Video-Upload fehlgeschlagen. Bitte erneut versuchen.', { title: 'Video-Upload fehlgeschlagen', danger: true }); }
      setUploadProg(null);
    };
    input.click();
  };

  // Strip formatting on paste
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const renderToolbar = (editorId) => (
    <div className="news-format-bar">
      <button type="button" className="fmt-btn" onClick={() => document.execCommand('bold')} title="Fett"><Bold size={14} /></button>
      <button type="button" className="fmt-btn" onClick={() => document.execCommand('italic')} title="Kursiv"><Italic size={14} /></button>
      <button type="button" className="fmt-btn" onClick={() => document.execCommand('insertUnorderedList')} title="Aufzählung"><ListIcon size={14} /></button>
      <button type="button" className="fmt-btn" onClick={() => document.execCommand('insertOrderedList')} title="Nummerierung">1.</button>
      <button type="button" className="fmt-btn" onClick={() => document.execCommand('formatBlock', false, 'h3')} title="Überschrift"><Type size={14} /></button>
      <button type="button" className="fmt-btn" onClick={insertEditorLink} title="Link einfügen"><Link2 size={14} /></button>
      <button type="button" className="fmt-btn" onClick={() => openVideoDialog(editorId)} title="Video einbetten (YouTube/Vimeo)"><Video size={14} /></button>
      <button type="button" className="fmt-btn" onClick={() => insertEditorImage(editorId)} title="Bild im Text einfügen"><Camera size={14} /></button>
      <button type="button" className="fmt-btn" onClick={() => insertEditorVideoFile(editorId)} title="Video-Datei hochladen & im Text einbetten (max. 1000 MB, wird automatisch auf 720p optimiert)" data-testid="insert-video-file-btn"><Film size={14} /></button>
      <button type="button" className="fmt-btn emoji-fmt" onClick={() => { setEditorEmojiTarget(editorId); setShowEditorEmoji(!showEditorEmoji); }} title="Emoji"><Smile size={14} /></button>
    </div>
  );

  return (
    <div className="tab-content" data-testid="courses-content">
      {!selectedCourse ? (
        <>
          <div className="section-header">
            <h2><BookOpen size={20} /> Projekte ({courses.length})</h2>
            {isAdminOrCoach && <button onClick={() => { setCourseForm({ title: '', short_description: '', description: '', image_url: '', visible_to: ['user', 'sponsor', 'coach'] }); setShowCreateCourse(true); }} className="btn-sm" data-testid="create-course-btn"><Plus size={16} /> Neues Projekt</button>}
          </div>
          <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Projekte durchsuchen..." />
          {courses.filter(c => matchesSearch(c, searchTerm, ['title', 'description'])).length === 0 ? (
            <div className="empty-state"><BookOpen size={32} /><p>{searchTerm ? 'Keine Treffer.' : 'Noch keine Projekte vorhanden.'}</p></div>
          ) : (
            <div className="courses-grid">
              {courses.filter(c => matchesSearch(c, searchTerm, ['title', 'description'])).map((c, i, arr) => (
                <div key={c.id} className="course-card" onClick={() => openCourse(c.id)} data-testid={`course-${c.id}`}>
                  {c.image_url && <div className="course-img" style={{ backgroundImage: `url(${c.image_url.startsWith('/') ? API_URL + c.image_url : c.image_url})` }} />}
                  <div className="course-info">
                    <h3>{c.title}</h3>
                    <p>{c.short_description || ''}</p>
                    {typeof c.lesson_count === 'number' && c.lesson_count > 0 && (
                      <div className="course-progress-mini" data-testid={`course-progress-${c.id}`}>
                        <div className="course-progress-mini-track">
                          <div className="course-progress-mini-fill" style={{ width: `${Math.round((c.completed_count / c.lesson_count) * 100)}%` }} />
                        </div>
                        <span>{c.completed_count === c.lesson_count ? '✓ Alle' : c.completed_count} von {c.lesson_count} Lektionen erledigt</span>
                      </div>
                    )}
                    {c.visible_to && c.visible_to.length < 3 && <div className="course-visibility"><Eye size={11} /> {c.visible_to.map(r => getRoleLabel(r)).join(', ')}</div>}
                    <span className="course-date">{formatDateShort(c.created_at)}</span>
                  </div>
                  {isAdminOrCoach && (
                    <div className="course-card-actions" onClick={e => e.stopPropagation()}>
                      <div className="course-reorder-btns">
                        {i > 0 && <button className="btn-icon-sm" onClick={() => handleMoveCourse(c.id, -1)} title="Nach oben"><ArrowUp size={13} /></button>}
                        {i < arr.length - 1 && <button className="btn-icon-sm" onClick={() => handleMoveCourse(c.id, 1)} title="Nach unten"><ArrowDown size={13} /></button>}
                      </div>
                      {isAdmin && <button className="btn-icon-sm danger" onClick={() => handleDeleteCourse(c.id)} data-testid={`del-course-${c.id}`}><Trash2 size={13} /></button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="course-top-bar" ref={detailTopRef}>
            <button className="btn-back" onClick={() => { setSelectedCourse(null); setSelectedLesson(null); }} data-testid="back-to-courses"><ChevronLeft size={16} /> Zurück</button>
            {isAdminOrCoach && <button className="btn-icon" data-testid="edit-course-btn" onClick={() => { setCourseForm({ title: selectedCourse.title, short_description: selectedCourse.short_description || '', description: selectedCourse.description, image_url: selectedCourse.image_url || '', visible_to: selectedCourse.visible_to || ['user','sponsor','coach'] }); setShowEditCourse(true); setFormError(''); }}><Pencil size={14} /> Projekt bearbeiten</button>}
          </div>
          <div className="course-detail">
            <div className="course-sidebar" ref={sidebarRef}>
              {courseLessons.length > 0 && (
                <div className="course-progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.round((courseProgress.length / courseLessons.length) * 100)}%` }} />
                  <span className="progress-text">{courseProgress.length}/{courseLessons.length} abgeschlossen</span>
                </div>
              )}
              {isAdminOrCoach && <button onClick={() => { setLessonForm({ title: '', content: '', video_urls: [], images: [], files: [] }); setShowCreateLesson(true); setFormError(''); }} className="btn-sm" data-testid="create-lesson-btn"><Plus size={16} /> Lektion</button>}
              <div className="lesson-list">
                <div className={`lesson-item description-item ${!selectedLesson ? 'active' : ''}`} onClick={() => selectLesson(null)} data-testid="lesson-description-item">
                  <span className="lesson-desc-icon"><BookOpen size={13} /></span>
                  <span className="lesson-title">Projektbeschreibung</span>
                </div>
                {courseLessons.map((l, i) => (
                  <div key={l.id} className={`lesson-item ${selectedLesson?.id === l.id ? 'active' : ''} ${courseProgress.includes(l.id) ? 'completed' : ''}`} onClick={() => selectLesson(l)} data-testid={`lesson-${l.id}`}>
                    <button className={`lesson-check ${courseProgress.includes(l.id) ? 'checked' : ''}`} onClick={e => { e.stopPropagation(); handleToggleProgress(l.id, e.currentTarget); }} title="Als erledigt markieren" data-testid={`lesson-check-${l.id}`}><Check size={12} /></button>
                    <span className="lesson-title">{l.title}</span>
                    {l.video_url && <Video size={12} />}
                    {l.file_url && <Download size={12} />}
                    {isAdminOrCoach && (
                      <div className="lesson-admin-btns">
                        <button className="btn-icon-sm" data-testid={`move-lesson-up-${l.id}`} onClick={e => { e.stopPropagation(); handleMoveLesson(l.id, -1); }} title="Nach oben"><ArrowUp size={11} /></button>
                        <button className="btn-icon-sm" data-testid={`move-lesson-down-${l.id}`} onClick={e => { e.stopPropagation(); handleMoveLesson(l.id, 1); }} title="Nach unten"><ArrowDown size={11} /></button>
                        <button className="btn-icon-sm" data-testid={`edit-lesson-${l.id}`} onClick={e => { e.stopPropagation(); setEditLessonForm({ id: l.id, title: l.title, content: l.content || '', video_urls: l.video_urls || (l.video_url ? [l.video_url] : []), images: l.images || (l.image_url ? [l.image_url] : []), files: l.files || (l.file_url ? [{url: l.file_url, name: l.file_name}] : []) }); setShowEditLesson(true); setFormError(''); }} title="Bearbeiten"><Pencil size={11} /></button>
                        {isAdmin && <button className="btn-icon-sm danger" onClick={e => { e.stopPropagation(); handleDeleteLesson(l.id); }} data-testid={`del-lesson-${l.id}`}><Trash2 size={11} /></button>}
                      </div>
                    )}
                  </div>
                ))}
                {courseLessons.length === 0 && <p className="hint-text">Noch keine Lektionen.</p>}
              </div>
            </div>
            <div className="lesson-viewer" ref={viewerRef}>
              {selectedLesson ? (
                <div data-testid="lesson-viewer">
                  <h3>{selectedLesson.title}</h3>
                  {(selectedLesson.video_urls || []).map((vurl, i) => {
                    const embedUrl = getEmbedUrl(vurl);
                    return embedUrl ? (
                      <div key={`vid-${i}-${vurl?.substring(0,20)}`} className="lesson-video">
                        <iframe src={embedUrl} title={selectedLesson.title} allowFullScreen frameBorder="0" />
                      </div>
                    ) : null;
                  })}
                  {selectedLesson.video_url && !(selectedLesson.video_urls || []).length && (() => {
                    const embedUrl = getEmbedUrl(selectedLesson.video_url);
                    return embedUrl ? (
                      <div className="lesson-video">
                        <iframe src={embedUrl} title={selectedLesson.title} allowFullScreen frameBorder="0" />
                      </div>
                    ) : null;
                  })()}
                  {(selectedLesson.images || []).map((imgUrl, i) => (
                    <img key={`img-${i}-${imgUrl?.substring(0,20)}`} src={imgUrl.startsWith('/') ? API_URL + imgUrl : imgUrl} alt="" className="lesson-img" />
                  ))}
                  {selectedLesson.image_url && !(selectedLesson.images || []).length && (
                    <img src={selectedLesson.image_url.startsWith('/') ? API_URL + selectedLesson.image_url : selectedLesson.image_url} alt={selectedLesson.title} className="lesson-img" />
                  )}
                  <SafeHtml className="lesson-content" data-testid="lesson-content" html={DOMPurify.sanitize(autoLinkUrls((selectedLesson.content || '').replace(/\n/g, '<br>')), {ADD_TAGS: ['iframe', 'video', 'source'], ADD_ATTR: ['allowfullscreen', 'frameborder', 'target', 'rel', 'style', 'controls', 'preload', 'playsinline']})} />
                  {(selectedLesson.files || []).map((f, i) => {
                    const fUrl = f.url && f.url.startsWith('/') ? `${API_URL}${f.url}` : f.url;
                    const isImg = /\.(jpe?g|png|webp|gif)$/i.test(f.name || f.url || '');
                    const isVid = isVideoFile(f.name || f.url);
                    return (
                      <div key={`file-${i}-${f.name}`} className="lesson-file-block">
                        {isVid && (
                          <video controls preload="metadata" playsInline className="lesson-video-player" data-testid={`lesson-video-player-${i}`}>
                            <source src={fUrl} />
                          </video>
                        )}
                        <a href={fUrl} download={f.name} className="lesson-download" target="_blank" rel="noreferrer" data-testid={`lesson-file-${i}`}><Download size={16} /> {f.name}</a>
                        {isImg && <img src={fUrl} alt={f.name} className="lesson-file-thumb" onClick={() => setFileLightbox(fUrl)} data-testid={`lesson-file-thumb-${i}`} />}
                      </div>
                    );
                  })}
                  {selectedLesson.file_url && !(selectedLesson.files || []).length && (() => {
                    const fUrl = selectedLesson.file_url.startsWith('/') ? `${API_URL}${selectedLesson.file_url}` : selectedLesson.file_url;
                    const isImg = /\.(jpe?g|png|webp|gif)$/i.test(selectedLesson.file_name || selectedLesson.file_url || '');
                    const isVid = isVideoFile(selectedLesson.file_name || selectedLesson.file_url);
                    return (
                      <div className="lesson-file-block">
                        {isVid && (
                          <video controls preload="metadata" playsInline className="lesson-video-player">
                            <source src={fUrl} />
                          </video>
                        )}
                        <a href={fUrl} download={selectedLesson.file_name} className="lesson-download" target="_blank" rel="noreferrer"><Download size={16} /> {selectedLesson.file_name || 'Datei herunterladen'}</a>
                        {isImg && <img src={fUrl} alt={selectedLesson.file_name} className="lesson-file-thumb" onClick={() => setFileLightbox(fUrl)} />}
                      </div>
                    );
                  })()}
                  <button className={`lesson-complete-btn ${courseProgress.includes(selectedLesson.id) ? 'done' : ''}`} onClick={e => handleToggleProgress(selectedLesson.id, e.currentTarget, true)} data-testid="lesson-complete-btn">
                    <Check size={16} /> {courseProgress.includes(selectedLesson.id) ? 'Erledigt' : 'Als erledigt markieren'}
                  </button>
                </div>
              ) : (
                <div className="course-info-panel" data-testid="course-info-panel">
                  <h2 className="course-info-title">{selectedCourse.title}</h2>
                  {selectedCourse.image_url && (
                    <img src={selectedCourse.image_url.startsWith('/') ? API_URL + selectedCourse.image_url : selectedCourse.image_url} alt={selectedCourse.title} className="course-info-image" />
                  )}
                  <SafeHtml className="course-info-description" data-testid="course-info-description" html={DOMPurify.sanitize(autoLinkUrls((selectedCourse.description || '').replace(/\n/g, '<br>')), {ADD_TAGS: ['iframe', 'video', 'source'], ADD_ATTR: ['allowfullscreen', 'frameborder', 'target', 'rel', 'style', 'controls', 'preload', 'playsinline']})} />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {celebration && <CelebrationOverlay title={celebration.title} onClose={closeCelebration} />}

      {/* Create Course Modal */}
      <Modal show={showCreateCourse} onClose={() => setShowCreateCourse(false)} title="Neuen Projekt erstellen" preventBackdropClose>
        <form onSubmit={handleCreateCourse} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Titel</label><input value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} required data-testid="course-title-input" /></div>
          <div className="form-group"><label>Kurzbeschreibung (für Übersicht)</label><input value={courseForm.short_description} onChange={e => setCourseForm({...courseForm, short_description: e.target.value})} placeholder="Kurze Beschreibung für die Projektübersicht" data-testid="course-short-desc" /></div>
          <div className="form-group"><label>Beschreibung</label>
            {renderToolbar("create-course-desc")}
            <div id="create-course-desc" className="wysiwyg-editor" contentEditable onPaste={handlePaste} data-placeholder="Projektbeschreibung..." data-testid="course-desc-input" />
          </div>
          <div className="form-group"><label>Titelbild</label>
            {courseForm.image_url && <img src={courseForm.image_url.startsWith('/') ? API_URL + courseForm.image_url : courseForm.image_url} alt="" className="form-img-preview" />}
            <label className="toolbar-btn"><Camera size={14} /> Bild hochladen<input type="file" accept="image/*" onChange={handleUploadCourseImage} hidden /></label>
            <UploadProgress progress={uploadProg} />
          </div>
          <div className="form-group"><label>Sichtbar fuer</label>
            <div className="checkbox-group">
              {['user', 'sponsor', 'coach'].map(role => (
                <label key={role} className="checkbox-label"><input type="checkbox" checked={courseForm.visible_to?.includes(role)} onChange={e => { const vt = courseForm.visible_to || []; setCourseForm({...courseForm, visible_to: e.target.checked ? [...vt, role] : vt.filter(r => r !== role)}); }} /> {getRoleLabel(role)}</label>
              ))}
              <span className="checkbox-hint">Botschafter sieht immer alles</span>
            </div>
          </div>
          <button type="submit" className="btn-login" disabled={formLoading} data-testid="course-submit"><Plus size={16} /> {formLoading ? 'Erstellen...' : 'Projekt erstellen'}</button>
        </form>
      </Modal>

      {/* Edit Course Modal */}
      <Modal show={showEditCourse} onClose={() => setShowEditCourse(false)} title="Projekt bearbeiten" preventBackdropClose>
        <form onSubmit={handleUpdateCourse} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Titel</label><input value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} required data-testid="course-title-edit" /></div>
          <div className="form-group"><label>Kurzbeschreibung (für Übersicht)</label><input value={courseForm.short_description} onChange={e => setCourseForm({...courseForm, short_description: e.target.value})} placeholder="Kurze Beschreibung für die Projektübersicht" /></div>
          <div className="form-group"><label>Beschreibung</label>
            {renderToolbar("edit-course-desc")}
            <div id="edit-course-desc" className="wysiwyg-editor" contentEditable onPaste={handlePaste} data-testid="course-desc-edit" ref={el => { if (el && !el.dataset.init) { el.innerHTML = DOMPurify.sanitize((courseForm.description || '').replace(/(background-color|color)\s*:\s*[^;"']+;?/gi, ''), {ADD_TAGS: ['iframe', 'video', 'source'], ADD_ATTR: ['allowfullscreen', 'frameborder', 'target', 'rel', 'style', 'controls', 'preload', 'playsinline']}); normalizeEditorMedia(el); el.dataset.init = '1'; } }} />
          </div>
          <div className="form-group"><label>Titelbild</label>
            {courseForm.image_url && <img src={courseForm.image_url.startsWith('/') ? API_URL + courseForm.image_url : courseForm.image_url} alt="" className="form-img-preview" />}
            <label className="toolbar-btn"><Camera size={14} /> Bild ändern<input type="file" accept="image/*" onChange={handleUploadCourseImage} hidden /></label>
            <UploadProgress progress={uploadProg} />
          </div>
          <div className="form-group"><label>Sichtbar fuer</label>
            <div className="checkbox-group">
              {['user', 'sponsor', 'coach'].map(role => (
                <label key={role} className="checkbox-label"><input type="checkbox" checked={courseForm.visible_to?.includes(role)} onChange={e => { const vt = courseForm.visible_to || []; setCourseForm({...courseForm, visible_to: e.target.checked ? [...vt, role] : vt.filter(r => r !== role)}); }} /> {getRoleLabel(role)}</label>
              ))}
              <span className="checkbox-hint">Botschafter sieht immer alles</span>
            </div>
          </div>
          <button type="submit" className="btn-login" disabled={formLoading}><Save size={16} /> {formLoading ? 'Speichern...' : 'Speichern'}</button>
        </form>
      </Modal>

      {/* Create Lesson Modal */}
      <Modal show={showCreateLesson} onClose={() => setShowCreateLesson(false)} title="Neue Lektion erstellen" preventBackdropClose>
        <form onSubmit={handleCreateLesson} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Titel</label><input value={lessonForm.title} onChange={e => setLessonForm({...lessonForm, title: e.target.value})} required data-testid="lesson-title-input" /></div>
          <div className="form-group"><label>Inhalt</label>
            {renderToolbar("create-lesson-content")}
            <div id="create-lesson-content" className="wysiwyg-editor" contentEditable onPaste={handlePaste} data-placeholder="Lektionsinhalt... (Videos und Bilder können über die Toolbar eingefügt werden)" data-testid="lesson-content-input" style={{minHeight: '400px'}} />
          </div>
          <div className="form-group">
            <label>Titelbild oder Titelvideo (optional, wird über dem Inhalt angezeigt)</label>
            {(lessonForm.video_urls || []).length > 0 && getEmbedUrl(lessonForm.video_urls[0]) ? (
              <div style={{position: 'relative'}}>
                <iframe src={getEmbedUrl(lessonForm.video_urls[0])} style={{width: '100%', height: '200px', borderRadius: '8px', border: '1px solid rgba(57,255,20,0.15)'}} frameBorder="0" allowFullScreen title="Vorschau" />
                <button type="button" className="remove-img-btn" style={{position: 'absolute', top: '0.5rem', right: '0.5rem'}} onClick={() => setLessonForm(p => ({...p, video_urls: []}))}><X size={14} /></button>
              </div>
            ) : (lessonForm.video_urls || []).length > 0 ? (
              <div className="multi-item"><span>{lessonForm.video_urls[0]}</span><span style={{color: '#FF0055', fontSize: '0.75rem', marginLeft: '0.5rem'}}>URL nicht erkannt</span><button type="button" className="btn-icon-sm danger" onClick={() => setLessonForm(p => ({...p, video_urls: []}))}><X size={12} /></button></div>
            ) : (lessonForm.images || []).length > 0 ? (
              <div style={{position: 'relative', display: 'inline-block'}}>
                <img src={lessonForm.images[0].startsWith('/') ? API_URL + lessonForm.images[0] : lessonForm.images[0]} alt="" style={{maxHeight: '120px', borderRadius: '8px'}} />
                <button type="button" className="remove-img-btn" style={{position: 'absolute', top: '0.3rem', right: '0.3rem'}} onClick={() => setLessonForm(p => ({...p, images: []}))}><X size={14} /></button>
              </div>
            ) : (
              <div style={{display: 'flex', gap: '0.5rem'}}>
                <div className="multi-add-row" style={{flex: 1}}><input placeholder="YouTube/Vimeo URL" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (e.target.value.trim()) { setLessonForm(p => ({...p, video_urls: [e.target.value.trim()], images: []})); e.target.value = ''; }}}} /><button type="button" className="btn-sm" onClick={e => { const input = e.target.closest('.multi-add-row').querySelector('input'); if (input && input.value.trim()) { setLessonForm(p => ({...p, video_urls: [input.value.trim()], images: []})); input.value = ''; }}}><Plus size={12} /></button></div>
                <label className="toolbar-btn" style={{whiteSpace: 'nowrap'}}><Camera size={14} /> Bild<input type="file" accept="image/*" onChange={e => handleUploadLessonFile(e, true, setLessonForm)} hidden /></label>
              </div>
            )}
          </div>
          <div className="form-group"><label>Dateien</label>
            {(lessonForm.files || []).map((f, i) => (
              <div key={`file-${i}-${f.name}`} className="multi-item"><Download size={12} /><span>{f.name}</span><button type="button" className="btn-icon-sm danger" onClick={() => setLessonForm(p => ({...p, files: p.files.filter((_, j) => j !== i)}))}><X size={12} /></button></div>
            ))}
            <label className="toolbar-btn"><Upload size={14} /> Datei hinzufügen<input type="file" onChange={e => handleUploadLessonFile(e, false, setLessonForm)} hidden /></label>
            <UploadProgress progress={uploadProg} />
          </div>
          <button type="submit" className="btn-login" disabled={formLoading} data-testid="lesson-submit"><Plus size={16} /> {formLoading ? 'Erstellen...' : 'Lektion erstellen'}</button>
        </form>
      </Modal>

      {/* Edit Lesson Modal */}
      <Modal show={showEditLesson} onClose={() => setShowEditLesson(false)} title="Lektion bearbeiten" preventBackdropClose>
        <form onSubmit={handleEditLesson} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Titel</label><input value={editLessonForm.title || ''} onChange={e => setEditLessonForm({...editLessonForm, title: e.target.value})} required data-testid="lesson-title-edit" /></div>
          <div className="form-group"><label>Inhalt</label>
            {renderToolbar("edit-lesson-content")}
            <div id="edit-lesson-content" className="wysiwyg-editor" contentEditable onPaste={handlePaste} data-testid="lesson-content-edit" ref={el => { if (el && !el.dataset.init) { el.innerHTML = DOMPurify.sanitize((editLessonForm.content || '').replace(/(background-color|color)\s*:\s*[^;"']+;?/gi, ''), {ADD_TAGS: ['iframe', 'video', 'source'], ADD_ATTR: ['allowfullscreen', 'frameborder', 'target', 'rel', 'style', 'controls', 'preload', 'playsinline']}); normalizeEditorMedia(el); el.dataset.init = '1'; } }} style={{minHeight: '400px'}} />
          </div>
          <div className="form-group">
            <label>Titelbild oder Titelvideo (optional, wird über dem Inhalt angezeigt)</label>
            {(editLessonForm.video_urls || []).length > 0 && getEmbedUrl(editLessonForm.video_urls[0]) ? (
              <div style={{position: 'relative'}}>
                <iframe src={getEmbedUrl(editLessonForm.video_urls[0])} style={{width: '100%', height: '200px', borderRadius: '8px', border: '1px solid rgba(57,255,20,0.15)'}} frameBorder="0" allowFullScreen title="Vorschau" />
                <button type="button" className="remove-img-btn" style={{position: 'absolute', top: '0.5rem', right: '0.5rem'}} onClick={() => setEditLessonForm(p => ({...p, video_urls: []}))}><X size={14} /></button>
              </div>
            ) : (editLessonForm.video_urls || []).length > 0 ? (
              <div className="multi-item"><span>{editLessonForm.video_urls[0]}</span><span style={{color: '#FF0055', fontSize: '0.75rem', marginLeft: '0.5rem'}}>URL nicht erkannt</span><button type="button" className="btn-icon-sm danger" onClick={() => setEditLessonForm(p => ({...p, video_urls: []}))}><X size={12} /></button></div>
            ) : (editLessonForm.images || []).length > 0 ? (
              <div style={{position: 'relative', display: 'inline-block'}}>
                <img src={editLessonForm.images[0].startsWith('/') ? API_URL + editLessonForm.images[0] : editLessonForm.images[0]} alt="" style={{maxHeight: '120px', borderRadius: '8px'}} />
                <button type="button" className="remove-img-btn" style={{position: 'absolute', top: '0.3rem', right: '0.3rem'}} onClick={() => setEditLessonForm(p => ({...p, images: []}))}><X size={14} /></button>
              </div>
            ) : (
              <div style={{display: 'flex', gap: '0.5rem'}}>
                <div className="multi-add-row" style={{flex: 1}}><input placeholder="YouTube/Vimeo URL" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (e.target.value.trim()) { setEditLessonForm(p => ({...p, video_urls: [e.target.value.trim()], images: []})); e.target.value = ''; }}}} /><button type="button" className="btn-sm" onClick={e => { const input = e.target.closest('.multi-add-row').querySelector('input'); if (input && input.value.trim()) { setEditLessonForm(p => ({...p, video_urls: [input.value.trim()], images: []})); input.value = ''; }}}><Plus size={12} /></button></div>
                <label className="toolbar-btn" style={{whiteSpace: 'nowrap'}}><Camera size={14} /> Bild<input type="file" accept="image/*" onChange={e => handleUploadLessonFile(e, true, setEditLessonForm)} hidden /></label>
              </div>
            )}
          </div>
          <div className="form-group"><label>Dateien</label>
            {(editLessonForm.files || []).map((f, i) => (
              <div key={`file-${i}-${f.name}`} className="multi-item"><Download size={12} /><span>{f.name}</span><button type="button" className="btn-icon-sm danger" onClick={() => setEditLessonForm(p => ({...p, files: p.files.filter((_, j) => j !== i)}))}><X size={12} /></button></div>
            ))}
            <label className="toolbar-btn"><Upload size={14} /> Datei hinzufügen<input type="file" onChange={e => handleUploadLessonFile(e, false, setEditLessonForm)} hidden /></label>
            <UploadProgress progress={uploadProg} />
          </div>
          <button type="submit" className="btn-login" disabled={formLoading}><Save size={16} /> {formLoading ? 'Speichern...' : 'Speichern'}</button>
        </form>
      </Modal>

      {/* Emoji Picker for editors */}
      {showEditorEmoji && editorEmojiTarget && (
        <div className="modal-overlay" onClick={() => setShowEditorEmoji(false)} style={{zIndex: 3000}}>
          <div className="emoji-picker-pro" onClick={e => e.stopPropagation()} style={{position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: '360px', width: '90vw'}}>
            <div className="emoji-cat-tabs">
              {Object.keys(EMOJI_CATS).map(cat => (
                <button key={cat} type="button" className={`emoji-cat-tab ${editorEmojiCat === cat ? 'active' : ''}`} onClick={() => setEditorEmojiCat(cat)}>{cat}</button>
              ))}
            </div>
            <div className="emoji-grid">
              {EMOJI_CATS[editorEmojiCat].map(em => (
                <button key={em} type="button" className="emoji-btn" onClick={() => {
                  const el = document.getElementById(editorEmojiTarget);
                  if (el) { el.focus(); document.execCommand('insertText', false, em); }
                  setShowEditorEmoji(false);
                }}>{em}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Custom Video Insert Dialog */}
      {showVideoDialog && (
        <div className="modal-overlay" onClick={() => setShowVideoDialog(false)} style={{zIndex: 3000}}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '480px'}}>
            <div className="modal-header">
              <h3>Video einbetten</h3>
              <button className="modal-close" onClick={() => setShowVideoDialog(false)}><X size={18} /></button>
            </div>
            <div style={{padding: '1.25rem'}}>
              <div className="form-group">
                <label>Video-URL (YouTube oder Vimeo)</label>
                <input value={videoDialogUrl} onChange={e => setVideoDialogUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=... oder https://vimeo.com/..." autoFocus onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmInsertVideo(); }}} data-testid="video-dialog-url" />
              </div>
              {videoDialogUrl && getEmbedUrlForInsert(videoDialogUrl) && (
                <div style={{marginBottom: '1rem', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(57,255,20,0.15)'}}>
                  <iframe src={getEmbedUrlForInsert(videoDialogUrl)} style={{width: '100%', height: '200px'}} frameBorder="0" allowFullScreen title="Vorschau" />
                </div>
              )}
              {videoDialogUrl && !getEmbedUrlForInsert(videoDialogUrl) && (
                <p style={{color: '#FF0055', fontSize: '0.82rem'}}>URL nicht erkannt. Unterstützt: YouTube, YouTube Shorts, Vimeo</p>
              )}
              <div style={{display: 'flex', gap: '0.5rem', justifyContent: 'flex-end'}}>
                <button type="button" className="btn-icon" onClick={() => setShowVideoDialog(false)}>Abbrechen</button>
                <button type="button" className="btn-sm" onClick={confirmInsertVideo} disabled={!videoDialogUrl || !getEmbedUrlForInsert(videoDialogUrl)}>Einfügen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {fileLightbox && (
        <div className="cal-lightbox" onClick={() => setFileLightbox(null)} data-testid="lesson-file-lightbox">
          <img src={fileLightbox} alt="Anhang" onClick={e => e.stopPropagation()} />
          <button className="cal-lightbox-close" onClick={() => setFileLightbox(null)} data-testid="lesson-file-lightbox-close"><X size={22} /></button>
        </div>
      )}
    </div>
  );
}
